/**
 * Pure planning helpers for the flipbook timeline bar. Frames ("pages") are
 * ordinary frame elements; sequence order is scene-array order — the same
 * order getFrameLikeElements / recordScreens use.
 */
import { getFrameLikeElements, isFrameLikeElement } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

/** Horizontal gap between flipbook pages on the canvas, px. */
export const FRAME_GAP = 80;

export type InsertPlan = {
  /** array index where the new frame element must be spliced in so it lands
   * after `ref` (and ref's members) but before the next frame */
  insertIndex: number;
  /** ids of later frames + their members that must shift right to make room */
  shiftIds: string[];
  /** how far the shifted elements (and the new frame) move, px */
  offsetX: number;
};

/**
 * Plans inserting a new page directly after `ref`: where in the scene array
 * the frame element goes, and which existing elements shift right by
 * `offsetX` to open a physical slot at `ref.x + ref.width + FRAME_GAP`.
 */
export const planInsertAfter = (
  elements: readonly ExcalidrawElement[],
  ref: ExcalidrawFrameLikeElement,
): InsertPlan => {
  // this book's pages only — a screen (or another book) interleaved with them
  // must not be counted as a slot or shifted when a page is added
  const frames = getFlipbookPages(elements, getPageBookId(ref) ?? undefined);
  const refOrder = frames.findIndex((f) => f.id === ref.id);
  const laterFrames = refOrder === -1 ? [] : frames.slice(refOrder + 1);
  const laterFrameIds = new Set(laterFrames.map((f) => f.id));
  const shiftIds = elements
    .filter(
      (el) =>
        laterFrameIds.has(el.id) ||
        (el.frameId !== null && laterFrameIds.has(el.frameId)),
    )
    .map((el) => el.id);

  // splice right before the next frame element in the array (members of a
  // frame never precede a later frame), else append
  const nextFrame = laterFrames[0];
  const insertIndex = nextFrame
    ? elements.findIndex((el) => el.id === nextFrame.id)
    : elements.length;

  return { insertIndex, shiftIds, offsetX: ref.width + FRAME_GAP };
};

export type ReorderPlan = {
  /** the full scene array with page chunks (frame + members) rearranged into
   * the new sequence order; same element references, ready to sync + set */
  elements: ExcalidrawElement[];
  /** position updates that move each page into its new physical slot */
  moves: { id: string; x: number; y: number }[];
};

/**
 * Plans a page reorder. Sequence order is scene-array order, so each page's
 * chunk (frame element + its members, in their original relative order) is
 * moved within the array; physical slots (the original frames' x/y origins,
 * in sequence order) are reassigned so page k of the new order sits where
 * page k of the old order was.
 * ponytail: elements not assigned to any frame keep their positions relative
 * to the whole page block, not to individual pages.
 */
export const planReorder = (
  elements: readonly ExcalidrawElement[],
  newOrderIds: readonly string[],
  bookId?: string,
): ReorderPlan => {
  // this book's pages only — reorder must not pull screens (or another book's
  // pages) into its slots
  const frames = getFlipbookPages(elements, bookId);
  const slots = frames.map((f) => ({ x: f.x, y: f.y }));
  const chunks = new Map<string, ExcalidrawElement[]>(
    frames.map((f) => [f.id, []]),
  );
  for (const el of elements) {
    if (chunks.has(el.id)) {
      chunks.get(el.id)!.push(el);
    } else if (el.frameId !== null && chunks.has(el.frameId)) {
      chunks.get(el.frameId)!.push(el);
    }
  }

  // splice the reordered chunk block in at the first chunk element's position
  const chunkMembers = new Set([...chunks.values()].flat().map((el) => el.id));
  const reordered: ExcalidrawElement[] = [];
  let inserted = false;
  for (const el of elements) {
    if (!chunkMembers.has(el.id)) {
      reordered.push(el);
      continue;
    }
    if (!inserted) {
      inserted = true;
      for (const id of newOrderIds) {
        reordered.push(...(chunks.get(id) ?? []));
      }
    }
  }

  const frameById = new Map(frames.map((f) => [f.id, f]));
  const moves: ReorderPlan["moves"] = [];
  newOrderIds.forEach((id, k) => {
    const frame = frameById.get(id);
    const slot = slots[k];
    if (!frame || !slot) {
      return;
    }
    const dx = slot.x - frame.x;
    const dy = slot.y - frame.y;
    if (dx === 0 && dy === 0) {
      return;
    }
    for (const el of chunks.get(id) ?? []) {
      moves.push({ id: el.id, x: el.x + dx, y: el.y + dy });
    }
  });

  return { elements: reordered, moves };
};

/** Ids to delete for "delete page": the frame plus its assigned members. */
export const deletePageIds = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
): Set<string> => {
  const ids = new Set<string>([frameId]);
  for (const el of elements) {
    if (el.frameId === frameId) {
      ids.add(el.id);
    }
  }
  return ids;
};

/** Ids to delete for "clear page": members only, the frame stays. */
export const clearPageIds = (
  elements: readonly ExcalidrawElement[],
  frameId: string,
): Set<string> => {
  const ids = deletePageIds(elements, frameId);
  ids.delete(frameId);
  return ids;
};

/**
 * A flipbook page and a screen are both frame elements — this marker is the
 * only thing that tells them apart. Unmarked frames read as screens, so
 * scenes authored before the flipbook existed keep every frame as a screen
 * and the flipbook simply starts empty.
 *
 * The value is the id of the BOOK the page belongs to, so one scene can hold
 * several independent flipbooks. Scenes written before books existed stored
 * `true`; those pages read as the legacy book below, which keeps them all in
 * one book exactly as they behaved before.
 */
export const FLIPBOOK_PAGE_KEY = "flipbookPage";

/** Book id that pre-book scenes (marker === `true`) migrate into. */
export const LEGACY_BOOK_ID = "book-1";

/** The book a frame belongs to, or null when it is not a flipbook page. */
export const getPageBookId = (el: ExcalidrawElement): string | null => {
  if (!isFrameLikeElement(el)) {
    return null;
  }
  const marker = el.customData?.[FLIPBOOK_PAGE_KEY];
  if (marker === true) {
    return LEGACY_BOOK_ID;
  }
  return typeof marker === "string" && marker !== "" ? marker : null;
};

export const isFlipbookPage = (el: ExcalidrawElement): boolean =>
  getPageBookId(el) !== null;

/**
 * Flipbook pages in sequence order (= scene-array order). Pass a `bookId` for
 * one book's pages; omit it for every page in the scene (used by the screen
 * selector, which only needs "is this frame a page at all").
 */
export const getFlipbookPages = (
  elements: readonly ExcalidrawElement[],
  bookId?: string,
): ExcalidrawFrameLikeElement[] =>
  getFrameLikeElements(elements).filter((f) => {
    const id = getPageBookId(f);
    return id !== null && (bookId === undefined || id === bookId);
  });

export type FlipbookBook = {
  id: string;
  /** display name, by order of first appearance: "Flipbook 1", "Flipbook 2"… */
  name: string;
  pageCount: number;
};

/** Every book in the scene, ordered by where its first page appears. */
export const getFlipbookBooks = (
  elements: readonly ExcalidrawElement[],
): FlipbookBook[] => {
  const counts = new Map<string, number>();
  for (const frame of getFrameLikeElements(elements)) {
    const id = getPageBookId(frame);
    if (id !== null) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return [...counts].map(([id, pageCount], index) => ({
    id,
    name: `Flipbook ${index + 1}`,
    pageCount,
  }));
};

/** Frames that are not flipbook pages — the screens. */
export const getScreens = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawFrameLikeElement[] => {
  const screens = getFrameLikeElements(elements).filter(
    (f) => !isFlipbookPage(f),
  );
  // Studio drag-and-drop writes customData.screenOrder; screens without one
  // keep scene order, after the explicitly ordered ones (stable sort)
  const orderOf = (f: ExcalidrawFrameLikeElement) => {
    const order = f.customData?.screenOrder;
    return typeof order === "number" && Number.isFinite(order)
      ? order
      : Infinity;
  };
  return screens
    .map((frame, index) => ({ frame, index }))
    .sort((a, b) => orderOf(a.frame) - orderOf(b.frame) || a.index - b.index)
    .map(({ frame }) => frame);
};

/** customData for a frame being created as (or promoted to) a page. */
export const flipbookPageCustomData = (
  existing?: Record<string, any> | null,
  bookId: string = LEGACY_BOOK_ID,
): Record<string, any> => ({
  ...(existing ?? {}),
  [FLIPBOOK_PAGE_KEY]: bookId,
});

/**
 * Frames that would become pages if the user accepts "adopt existing
 * frames" — i.e. every currently unmarked frame, in scene order.
 */
export const planAdoptFrames = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawFrameLikeElement[] => getScreens(elements);

/** Vertical gap between one book's row of pages and the next book's. */
export const BOOK_GAP = 160;

/**
 * Where a new book's first page goes: directly below every existing page, so
 * books read as separate rows on the canvas instead of piling up on each
 * other. Falls back to the given origin when the scene has no pages yet.
 */
export const planNewBookOrigin = (
  elements: readonly ExcalidrawElement[],
  fallback: { x: number; y: number },
): { x: number; y: number } => {
  const pages = getFlipbookPages(elements);
  if (pages.length === 0) {
    return fallback;
  }
  let minX = Infinity;
  let maxY = -Infinity;
  for (const page of pages) {
    minX = Math.min(minX, page.x);
    maxY = Math.max(maxY, page.y + page.height);
  }
  return { x: minX, y: maxY + BOOK_GAP };
};

export { getFrameLikeElements, isFrameLikeElement };
